import { useEffect } from "react";
import { X, MapPin, Hotel, Utensils, Coffee, CarFront, Info, Calendar } from 'lucide-react';
import { CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { PackageItem } from "@/types/package";

interface Props {
    pkg: PackageItem;
    onClose: () => void;
}

const InfoKonvensiFeesModal = ({ pkg, onClose }: Props) => {
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [onClose]);

    // Format tanggal sederhana jika diperlukan
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <DialogPrimitive.Root open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Content asChild>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm overflow-y-auto"
                        style={{ pointerEvents: 'auto' }}
                        onClick={onClose}
                    >
                        <div className="min-h-full w-full flex items-center justify-center p-0 md:p-10">
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 50, scale: 0.95 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="relative w-full max-w-2xl bg-white dark:bg-slate-950 md:rounded-[2.5rem] shadow-2xl overflow-hidden my-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 z-[120] p-2 bg-black/20 hover:bg-black/40 text-white rounded-full backdrop-blur-md transition-all md:flex hidden"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <div className="flex flex-col">
                        <div className="relative h-64 md:h-72 w-full">
                            <img src={pkg.photo_url} className="w-full h-full object-cover" alt={pkg.name} />
                            <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-slate-950 via-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-8">
                                <Badge className="bg-emerald-500 mb-2 border-none font-bold">PACKAGE DETAIL</Badge>
                                <h2 className="text-2xl md:text-3xl font-black text-blue-900 dark:text-white uppercase leading-tight">
                                    {pkg.name}
                                </h2>
                            </div>
                        </div>

                        <CardContent className="p-8 md:p-10 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className={`space-y-2 ${Number(pkg.price) > 0 ? "md:col-span-2" : "md:col-span-3"}`}>
                                    <div className="flex items-center gap-2 text-blue-600">
                                        <Info className="w-4 h-4" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">Deskripsi</span>
                                    </div>
                                    <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed italic">
                                        "{pkg.description}"
                                    </p>
                                </div>
                                {Number(pkg.price) > 0 && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-[1.5rem] border border-blue-100 dark:border-blue-800 flex flex-col justify-center">
                                        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block mb-1">Harga</span>
                                        <p className="text-xl font-black text-blue-700 dark:text-blue-300">
                                            Rp {Number(pkg.price).toLocaleString("id-ID")}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Tanggal Pelaksanaan */}
                            {(pkg.start_date || pkg.end_date) && (
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Tanggal Pelaksanaan</h4>
                                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800">
                                        <div className="flex-shrink-0 text-blue-600 bg-white dark:bg-slate-800 p-2 rounded-xl shadow-sm">
                                            <Calendar className="w-4 h-4" />
                                        </div>
                                        <div className="space-y-0.5">
                                            <p className="text-sm font-bold text-blue-900 dark:text-blue-100">
                                                {formatDate(pkg.start_date)}
                                                {pkg.end_date && pkg.end_date !== pkg.start_date ? ` - ${formatDate(pkg.end_date)}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Jadwal Kegiatan */}
                            {pkg.itineraries && pkg.itineraries.length > 0 && (
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Jadwal Kegiatan</h4>
                                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-800">
                                        <div className="relative border-l-2 border-emerald-100 dark:border-emerald-900 ml-2 space-y-5">
                                            {pkg.itineraries.map((itinerary, idx) => (
                                                <div key={idx} className="relative pl-6">
                                                    {/* Timeline Dot */}
                                                    <div className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-emerald-500 ring-4 ring-emerald-50 dark:ring-emerald-900/50" />

                                                    <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4">
                                                        <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 shrink-0 w-24">
                                                            {itinerary.start_time?.slice(0, 5) || '--:--'} {itinerary.end_time ? `- ${itinerary.end_time.slice(0, 5)}` : ''}
                                                        </div>
                                                        <div className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-tight">
                                                            {itinerary.activity}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Facilities Grid */}
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Fasilitas Termasuk</h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {facilities.map((item: any) => (
                                        <div key={item.id} className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800">
                                            <div className="flex-shrink-0 text-blue-600 bg-white dark:bg-slate-800 p-2 rounded-xl shadow-sm">
                                                {item.icon}
                                            </div>
                                            <div className="space-y-0.5">
                                                <p className="text-xs font-bold dark:text-white">{item.title}</p>
                                                <p className="text-[10px] text-slate-500 leading-tight">{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="pt-6 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-rose-500" />
                                    <span className="text-[10px] font-black uppercase text-slate-500">Nusa Dua, Bali</span>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="md:hidden px-6 py-3 bg-slate-900 text-white rounded-xl text-xs font-bold"
                                >
                                    Tutup
                                </button>
                            </div>
                        </CardContent>
                    </div>
                </motion.div>
            </div>
        </motion.div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

const facilities = [
    { id: 1, icon: <MapPin className="w-4 h-4" />, title: "Lokasi", desc: "BNDCC Convention Center." },
    { id: 2, icon: <Hotel className="w-4 h-4" />, title: "Akomodasi", desc: "Hotel Bintang 4." },
    { id: 3, icon: <Utensils className="w-4 h-4" />, title: "Konsumsi", desc: "Buffet Lunch & Dinner." },
    { id: 4, icon: <Coffee className="w-4 h-4" />, title: "Snack", desc: "Exclusive Coffee Break." },
    { id: 5, icon: <CarFront className="w-4 h-4" />, title: "Transport", desc: "Antar-jemput Bandara." }
];

export default InfoKonvensiFeesModal;